import React from 'react';
import {
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '../../../../../../common/modals/modal-helpers';
import { compose, withHandlers, withStateHandlers } from 'recompose';
import {
  LocalSystemFilterItem,
  AddFilterType,
  UpdateFilterType,
} from '../../../sharedFilterTypes';
import { HandlerMethodTypes } from '@mm/utils';
import { FILTER_TYPES } from '@mm/state/model/AudiencesModel';
import './reachableModal.scss';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import classNames from 'classnames';

type OuterProps = {
  toggle: () => void;
  onFinish: AddFilterType;
  onUpdateFilter: UpdateFilterType;
  selectedFilter?: LocalSystemFilterItem;
  selectedFilterId?: number | null;
  nonModalUse?: boolean;
  className?: string;
};

type StateProps = {
  filterValue: boolean;
};

type StateHandlerProps = HandlerMethodTypes<OuterProps, typeof stateUpdaters>;

type PropsBeforeWithHandlers = OuterProps & StateProps & StateHandlerProps;

type HanldersProps0 = HandlerMethodTypes<
  PropsBeforeWithHandlers,
  typeof handlers0
>;
type HanldersProps1 = HandlerMethodTypes<
  PropsBeforeWithHandlers,
  typeof handlers1
>;

type PropsFromHanlders = HanldersProps0 & HanldersProps1;

type InnerProps = PropsBeforeWithHandlers & PropsFromHanlders;

type Props = OuterProps & InnerProps;

const onClose =
  ({ toggle, setFilterValue }: PropsBeforeWithHandlers) =>
  () => {
    setFilterValue(true);
    toggle();
  };

const onSave =
  ({ onClose, onFinish }: PropsBeforeWithHandlers & HanldersProps0) =>
  (filterValue: boolean) => {
    onFinish({
      value: [`${filterValue}`],
      filterType: FILTER_TYPES.reachable,
    });
    if (onClose) {
      onClose();
    }
  };

const onUpdate =
  ({
    onClose,
    onUpdateFilter,
    selectedFilterId,
    filterValue,
  }: PropsBeforeWithHandlers & HanldersProps0) =>
  () => {
    if (selectedFilterId) {
      onUpdateFilter(selectedFilterId, {
        value: [`${filterValue}`],
        filterType: FILTER_TYPES.reachable,
      });
    }
    onClose();
  };

const handlers0 = {
  onClose,
};

const handlers1 = {
  onSave,
  onUpdate,
};

const getInitialState = ({ selectedFilter }: OuterProps) => ({
  filterValue:
    selectedFilter && selectedFilter.value[0] === 'false' ? false : true,
});

const stateUpdaters = {
  setFilterValue: () => (filterValue: boolean) => ({ filterValue }),
};

const enhance = compose<InnerProps, OuterProps>(
  withStateHandlers<StateProps, StateHandlerProps, OuterProps>(
    getInitialState,
    stateUpdaters,
  ),
  withHandlers(handlers0),
  withHandlers(handlers1),
);

const ReachableModalPure = ({
  selectedFilterId,
  setFilterValue,
  filterValue,
  onSave,
  onUpdate,
  onClose,
  nonModalUse,
  className,
}: Props) => {
  const header = (
    <span className="reachable-filter-modal__header-content">
      {selectedFilterId ? 'Update' : 'Create'} Filter Reachable by Promotional
      Chat Blast
    </span>
  );

  const body = (
    <>
      <div className="reachable-filter-modal__input-container">
        <input
          type="radio"
          checked={filterValue}
          onChange={() => setFilterValue(true)}
          className="reachable-filter-modal__radio-input"
        />
        Reachable
      </div>
      <div className="reachable-filter-modal__input-container">
        <input
          type="radio"
          checked={!filterValue}
          onChange={() => setFilterValue(false)}
          className="reachable-filter-modal__radio-input"
        />
        Not Reachable
      </div>
    </>
  );

  const footer = (
    <div className="reachable-filter-modal__buttons">
      <PrimaryButton color={ButtonColor.secondaryBlue} onClick={onClose}>
        Cancel
      </PrimaryButton>
      {!selectedFilterId && (
        <PrimaryButton onClick={() => onSave(filterValue)}>Save</PrimaryButton>
      )}
      {selectedFilterId && (
        <PrimaryButton onClick={onUpdate}>Update</PrimaryButton>
      )}
    </div>
  );

  let content = (
    <>
      <ModalHeader className="reachable-filter-modal__header" toggle={onClose}>
        {header}
      </ModalHeader>
      <ModalBody className="reachable-filter-modal__container">
        {body}
      </ModalBody>
      <ModalFooter className="reachable-filter-modal__footer">
        {footer}
      </ModalFooter>
    </>
  );

  if (nonModalUse) {
    content = (
      <>
        {body}
        {footer}
      </>
    );
  }

  return (
    <div className={classNames(className, 'reachable-filter-modal')}>
      {content}
    </div>
  );
};

export const ReachableModal = enhance(ReachableModalPure);
